import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Navbar from '../components/Navbar';
import { useUser } from '../contexts/UserContext';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Empty string for production (relative URL)
  : 'http://localhost:5001';

const ProfilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refreshUser } = useUser();
  
  // Form state
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Field validation states
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [usernameValid, setUsernameValid] = useState(false);
  const [emailValid, setEmailValid] = useState(false);
  
  // Debounce timers
  const usernameTimer = useRef(null);
  const emailTimer = useRef(null);
  
  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setUsername(user.username || '');
      setEmail(user.email || '');
      setIsLoading(false);
      
      // Initialize validation status for existing values
      setUsernameValid(true);
      setEmailValid(true);
      
      // Clear any existing errors when user data is loaded
      setErrors({});
    }
  }, [user]);
  
  // Check username uniqueness
  const checkUsernameUniqueness = async (value) => {
    // Skip validation if unchanged
    if (value === user?.username) {
      setUsernameValid(true);
      return;
    }
    
    setIsCheckingUsername(true);
    setUsernameValid(false);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/check-username?username=${encodeURIComponent(value)}`);
      const data = await response.json();
      
      if (response.ok) {
        if (data.exists) {
          setErrors(prev => ({ ...prev, username: t('profilePage.usernameInUse', 'Username already in use') }));
          setUsernameValid(false);
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.username;
            return newErrors;
          });
          setUsernameValid(true);
        }
      }
    } catch (error) {
      console.error('Error checking username uniqueness:', error);
      setUsernameValid(false);
    } finally {
      setIsCheckingUsername(false);
    }
  };
  
  // Check email uniqueness
  const checkEmailUniqueness = async (value) => {
    // Skip validation if unchanged
    if (value === user?.email) {
      setEmailValid(true);
      return;
    }
    
    setIsCheckingEmail(true);
    setEmailValid(false);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/check-email?email=${encodeURIComponent(value)}`);
      const data = await response.json();
      
      if (response.ok) {
        if (data.exists) {
          setErrors(prev => ({ ...prev, email: t('profilePage.emailInUse', 'Email already in use') }));
          setEmailValid(false);
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.email;
            return newErrors;
          });
          setEmailValid(true);
        }
      }
    } catch (error) {
      console.error('Error checking email uniqueness:', error);
      setEmailValid(false);
    } finally {
      setIsCheckingEmail(false);
    }
  };
  
  // Handle username change with debounce
  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    
    // Clear existing errors and timer
    clearTimeout(usernameTimer.current);
    
    // Basic validation
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, username: t('profilePage.usernameRequired', 'Username is required') }));
      return;
    }
    
    // Check for valid username format
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      setErrors(prev => ({ 
        ...prev, 
        username: t('profilePage.usernameInvalid', 'Username can only contain letters, numbers, and underscores') 
      }));
      return;
    }
    
    // Debounce the API call
    usernameTimer.current = setTimeout(() => {
      checkUsernameUniqueness(value);
    }, 500);
  };
  
  // Handle email change with debounce
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Clear existing errors and timer
    clearTimeout(emailTimer.current);
    
    // Basic validation
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, email: t('profilePage.emailRequired', 'Email is required') }));
      return;
    } 
    
    // If it's a valid email format, immediately clear the invalid email error
    if (/\S+@\S+\.\S+/.test(value)) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.email === t('profilePage.emailInvalid', 'Email is invalid')) {
          delete newErrors.email;
        }
        return newErrors;
      });
    } else {
      setErrors(prev => ({ ...prev, email: t('profilePage.emailInvalid', 'Email is invalid') }));
      return;
    }
    
    // Debounce the API call
    emailTimer.current = setTimeout(() => {
      checkEmailUniqueness(value);
    }, 500);
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = t('profilePage.nameRequired', 'Name is required');
    }
    
    if (!username.trim()) {
      newErrors.username = t('profilePage.usernameRequired', 'Username is required');
    }
    
    if (!email.trim()) {
      newErrors.email = t('profilePage.emailRequired', 'Email is required');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('profilePage.emailInvalid', 'Email is invalid');
    }
    
    // Check for existing validation errors
    if (errors.username) {
      newErrors.username = errors.username;
    }
    
    if (errors.email) {
      newErrors.email = errors.email;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const validatePasswordForm = () => {
    const newErrors = {};
    
    if (!currentPassword) {
      newErrors.currentPassword = t('profilePage.currentPasswordRequired', 'Current password is required');
    }
    
    if (!newPassword) {
      newErrors.newPassword = t('profilePage.newPasswordRequired', 'New password is required');
    } else if (newPassword.length < 12) {
      newErrors.newPassword = t('profilePage.passwordTooShort', 'Password must be at least 12 characters');
    }
    
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t('profilePage.passwordsDoNotMatch', 'Passwords do not match');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          name,
          username,
          email
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (data.error === 'Email already in use') {
          setErrors({ email: t('profilePage.emailInUse', 'Email already in use') });
        } else if (data.error === 'Username already in use') {
          setErrors({ username: t('profilePage.usernameInUse', 'Username already in use') });
        } else {
          toast.error(data.error || t('profilePage.updateFailed', 'Failed to update profile'));
        }
        return;
      }
      
      // Optimistically update cached user data before refreshing from API
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = { 
        ...currentUser, 
        name, 
        username, 
        email 
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Refresh user data from API
      await refreshUser();
      
      // Reset validation states
      setUsernameValid(true);
      setEmailValid(true);
      
      toast.success(t('profilePage.updateSuccess', 'Profile updated successfully'));
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('profilePage.updateFailed', 'Failed to update profile'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (data.error === 'Current password is incorrect') {
          setErrors({ currentPassword: t('profilePage.incorrectPassword', 'Current password is incorrect') });
        } else {
          toast.error(data.error || t('profilePage.passwordChangeFailed', 'Failed to change password'));
        }
        return;
      }
      
      // Reset password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsChangingPassword(false);
      
      toast.success(t('profilePage.passwordChangeSuccess', 'Password changed successfully'));
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(t('profilePage.passwordChangeFailed', 'Failed to change password'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto p-6 mt-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto p-6 mt-8">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-2xl font-bold mb-6">{t('profilePage.title', 'My Profile')}</h1>
          
          <div className="mb-8">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profilePage.name', 'Name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>
                
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profilePage.username', 'Username')}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="username"
                      value={username}
                      onChange={handleUsernameChange}
                      className={`w-full p-2 border ${errors.username ? 'border-red-500' : usernameValid ? 'border-green-500' : 'border-gray-300'} rounded-md focus:ring-blue-500 focus:border-blue-500 pr-10`}
                    />
                    {usernameValid && !isCheckingUsername && !errors.username && (
                      <svg className="absolute right-3 top-3 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  {isCheckingUsername && (
                    <p className="mt-1 text-sm text-gray-500">
                      {t('profilePage.checking', 'Checking...')}
                    </p>
                  )}
                  {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username}</p>}
                  {!errors.username && !isCheckingUsername && (
                    <p className="mt-1 text-xs text-gray-500">
                      {t('profilePage.usernameHint', 'Use only letters, numbers, and underscores')}
                    </p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profilePage.email', 'Email')}
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={handleEmailChange}
                      className={`w-full p-2 border ${errors.email ? 'border-red-500' : emailValid ? 'border-green-500' : 'border-gray-300'} rounded-md focus:ring-blue-500 focus:border-blue-500 pr-10`}
                    />
                    {emailValid && !isCheckingEmail && !errors.email && (
                      <svg className="absolute right-3 top-3 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  {isCheckingEmail && (
                    <p className="mt-1 text-sm text-gray-500">
                      {t('profilePage.checking', 'Checking...')}
                    </p>
                  )}
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none transition-colors disabled:bg-blue-400"
              >
                {isSubmitting 
                  ? t('profilePage.saving', 'Saving...') 
                  : t('profilePage.saveChanges', 'Save Changes')}
              </button>
            </form>
          </div>
          
          <div className="border-t pt-6">
            <h2 className="text-xl font-semibold mb-4">{t('profilePage.changePassword', 'Change Password')}</h2>
            
            {!isChangingPassword ? (
              <button
                onClick={() => setIsChangingPassword(true)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none transition-colors"
              >
                {t('profilePage.changePasswordButton', 'Change Password')}
              </button>
            ) : (
              <form onSubmit={handlePasswordChange}>
                <div className="grid grid-cols-1 gap-6 mb-6">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profilePage.currentPassword', 'Current Password')}
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    {errors.currentPassword && <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profilePage.newPassword', 'New Password')}
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    {errors.newPassword && <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>}
                    <p className="mt-1 text-sm text-gray-500">
                      {t('profilePage.passwordRequirements', 'Minimum 12 characters')}
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profilePage.confirmPassword', 'Confirm New Password')}
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none transition-colors disabled:bg-blue-400"
                  >
                    {isSubmitting 
                      ? t('profilePage.changing', 'Changing...') 
                      : t('profilePage.updatePassword', 'Update Password')}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setIsChangingPassword(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setErrors({});
                    }}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none transition-colors"
                  >
                    {t('profilePage.cancel', 'Cancel')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage; 